import { ITENS_POR_SETOR, MACROS, SETORES, UNIDADES } from "@caint/data";
import { Header } from "@caint/design-system";
import type { EscopoData } from "@caint/shared";
import { SETORES_OBRIGATORIOS } from "@caint/shared";
import { Fragment, useMemo } from "react";
import { notaPreenchida } from "../lib/supervisao.ts";
import type { ItemMerged } from "../state/useAppState.ts";

export interface EscopoSetoresProps {
  escopo: EscopoData;
  /** Catálogo completo (mesclado) — p/ contar itens já respondidos por setor. */
  items: ItemMerged[];
  /** (De)seleciona um setor (obrigatórios travados). */
  alternarSetor: (setor: string) => void;
  /** Confirma o escopo e libera o checklist. */
  confirmarEscopo: () => void;
  /** Edição liberada só com ciclo aberto/na janela. */
  editavel: boolean;
  /** Navega para o checklist após confirmar (ou ao voltar de uma reabertura). */
  onPronto: () => void;
  /** Id da unidade (resolve o nome no cabeçalho). */
  unidadeId?: string;
}

/**
 * Pré-avaliação (doc 17b §5): o avaliador declara QUAIS dos 18 setores existem
 * na unidade ANTES de abrir o checklist. Obrigatórios (05A/05C) ficam travados;
 * a sugestão (do ciclo anterior/cadastro) vem pré-aplicada e editável. "Fora de
 * escopo" ≠ "NA": o setor inexistente é declarado aqui, uma vez, e é auditável.
 */
export function EscopoSetores({
  escopo,
  items,
  alternarSetor,
  confirmarEscopo,
  editavel,
  onPronto,
  unidadeId,
}: EscopoSetoresProps) {
  const unidadeNome = UNIDADES.find((u) => u.id === unidadeId)?.nome ?? "Unidade";
  const ativos = useMemo(() => new Set(escopo.setoresAtivos), [escopo.setoresAtivos]);
  // Nº de itens já respondidos (nota do avaliador) por setor — para o aviso ao desmarcar.
  const respondidosPorSetor = useMemo(() => {
    const m: Record<string, number> = {};
    for (const it of items) {
      if (notaPreenchida(it.mi)) {
        m[it.sector] = (m[it.sector] ?? 0) + 1;
      }
    }
    return m;
  }, [items]);

  const nSetores = escopo.setoresAtivos.length;
  const nItens = escopo.setoresAtivos.reduce((n, c) => n + (ITENS_POR_SETOR[c] ?? 0), 0);
  const jaConfirmado = escopo.escopoConfirmado;

  const aoAlternar = (code: string, ativoAgora: boolean) => {
    if (!editavel) {
      return;
    }
    // Desmarcar um setor com respostas: confirma (as respostas NÃO são apagadas —
    // ficam fora do cálculo e voltam ao re-selecionar). Doc 17b §5.3 / §6.
    const respondidos = respondidosPorSetor[code] ?? 0;
    if (ativoAgora && respondidos > 0 && typeof window !== "undefined") {
      const ok = window.confirm(
        `Este setor tem ${respondidos} item(ns) já respondido(s). Ao marcá-lo como inexistente, eles saem do cálculo (não são apagados e voltam se você re-selecionar). Continuar?`,
      );
      if (!ok) {
        return;
      }
    }
    alternarSetor(code);
  };

  return (
    <Fragment>
      <Header title="Setores da Unidade" sub={unidadeNome} />
      <div className="m-body">
        <div
          className="m-card pad"
          style={{ marginBottom: 12, fontSize: 13, lineHeight: 1.45, color: "var(--text-muted)" }}
        >
          Marque <b>quais setores existem nesta unidade</b>. O checklist e a amostra do supervisor passam a
          cobrir só os setores ativos. Setor inexistente ≠ item “não se aplica” (NA).
        </div>

        {!editavel && (
          <div
            className="m-card pad"
            style={{
              marginBottom: 12,
              fontSize: 12.5,
              color: "var(--text-muted)",
              background: "var(--surface-2)",
            }}
          >
            👁️ Somente leitura — esta unidade não tem ciclo aberto na janela atual.
          </div>
        )}

        <div className="m-list">
          {SETORES.map((s) => {
            const ativo = ativos.has(s.code);
            const obrig = SETORES_OBRIGATORIOS.includes(s.code);
            const respondidos = respondidosPorSetor[s.code] ?? 0;
            return (
              <button
                key={s.code}
                type="button"
                className="m-card pad"
                disabled={!editavel || obrig}
                onClick={() => aoAlternar(s.code, ativo)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 6,
                  cursor: !editavel || obrig ? "default" : "pointer",
                  opacity: ativo ? 1 : 0.55,
                  border: ativo ? "1px solid var(--teal-300)" : "0.5px solid var(--border)",
                  background: "var(--surface)",
                  color: "var(--text)",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 14,
                    background: ativo ? "var(--teal-500, var(--teal-300))" : "transparent",
                    border: ativo ? "none" : "1.5px solid var(--border)",
                    color: "#fff",
                  }}
                >
                  {ativo ? "✓" : ""}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, fontSize: 14.5 }}>{s.nome}</span>
                  <span className="spec" style={{ display: "block" }}>
                    {s.code} · Macro {s.macro} ({MACROS[s.macro]}) · {ITENS_POR_SETOR[s.code] ?? 0} itens
                    {obrig ? " · obrigatório" : ""}
                    {!ativo && respondidos > 0 ? ` · ${respondidos} resposta(s) preservada(s)` : ""}
                  </span>
                </span>
                {obrig && <span className="spec">🔒</span>}
              </button>
            );
          })}
        </div>

        <div
          className="m-card pad"
          style={{ marginTop: 12, display: "flex", justifyContent: "space-between", fontSize: 13 }}
        >
          <b>
            {nSetores} setores ativos · {nItens} itens no escopo
          </b>
        </div>

        <button
          type="button"
          className="m-btn primary"
          style={{ marginTop: 14, width: "100%" }}
          disabled={!editavel || nSetores === 0}
          onClick={() => {
            confirmarEscopo();
            onPronto();
          }}
        >
          {jaConfirmado ? "Salvar setores e voltar ao checklist" : "Confirmar e abrir checklist"}
        </button>
      </div>
    </Fragment>
  );
}
